'use client'

import { useState, useEffect, useRef, useCallback } from 'react'

// ── Constants ────────────────────────────────────────────────────────────────
const SCREEN_MS  = 4000  // visible time per screen
const FADE_MS    = 220   // fade out duration

const FEATURES = [
  { icon: '⚾', label: 'Team management'    },
  { icon: '📅', label: 'Schedule & calendar' },
  { icon: '💳', label: 'Payment collection'  },
  { icon: '💬', label: 'Team chat'           },
  { icon: '🏆', label: 'Live scorekeeping'   },
  { icon: '💰', label: 'Financial dashboard' },
]

// ── Shared bottom nav ────────────────────────────────────────────────────────
function BottomNav({ active }: { active: 'Home' | 'Schedule' | 'Roster' | 'Pay' | 'More' }) {
  const tabs = ['Home', 'Schedule', 'Roster', 'Pay', 'More'] as const
  return (
    <div style={{
      backgroundColor: '#fff', borderTop: '1px solid #f1f5f9',
      boxShadow: '0 -4px 16px rgba(0,0,0,0.05)',
      display: 'flex', paddingTop: 6, paddingBottom: 4, flexShrink: 0,
    }}>
      {tabs.map(tab => (
        <div key={tab} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: tab === active ? '#F5E642' : '#cbd5e1' }} />
          </div>
          <span style={{ fontSize: 8, fontWeight: tab === active ? 700 : 500, color: tab === active ? '#F5E642' : '#94a3b8' }}>{tab}</span>
        </div>
      ))}
    </div>
  )
}

// ── App header ────────────────────────────────────────────────────────────────
function AppHeader({ title, subtitle }: { title?: string; subtitle?: string }) {
  return (
    <div style={{
      backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)', flexShrink: 0,
    }}>
      <div style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px' }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px' }}>chalk</span>
          <span style={{ fontSize: 15, fontWeight: 900, color: '#F5E642' }}>.</span>
        </div>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #F5E642 0%, #f59e0b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: '#13201B' }}>JR</span>
        </div>
      </div>
      {title && (
        <div style={{ padding: '8px 14px 10px', borderTop: '1px solid #f8fafc' }}>
          {subtitle && <p style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{subtitle}</p>}
          <h1 style={{ fontSize: 17, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.4px', lineHeight: 1.1 }}>{title}</h1>
        </div>
      )}
    </div>
  )
}

// ── SCREEN 1: Team Dashboard ─────────────────────────────────────────────────
function Screen1Dashboard() {
  const MODULES = [
    { icon: '👥', label: 'Roster',   color: '#eff6ff', border: '#bfdbfe' },
    { icon: '📅', label: 'Schedule', color: '#f0fdf4', border: '#bbf7d0' },
    { icon: '💳', label: 'Pay',      color: '#fefce8', border: '#fef08a' },
    { icon: '💬', label: 'Chat',     color: '#fdf4ff', border: '#e9d5ff' },
    { icon: '🏆', label: 'Game Day', color: '#fff1f2', border: '#fecdd3' },
    { icon: '💰', label: 'Treasury', color: '#f0fdfa', border: '#99f6e4' },
  ]
  return (
    <div style={{ background: '#f7f8fa', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' }}>
      {/* Team hero */}
      <div style={{ backgroundColor: '#fff', padding: '14px 14px 10px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flexShrink: 0 }}>⚾</div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 1 }}>Baseball · Spring 2026</p>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.4px', lineHeight: 1.1 }}>Aurora Titans</h2>
          </div>
          <span style={{ fontSize: 9, fontWeight: 700, color: '#F5E642', backgroundColor: 'rgba(250,204,21,0.12)', borderRadius: 6, padding: '3px 8px', border: '1px solid rgba(250,204,21,0.25)' }}>COACH</span>
        </div>
        {/* Stats row */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          {[
            { v: '14', label: 'Players' },
            { v: '3', label: 'This week' },
            { v: 'AUR-X9K', label: 'Join code' },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, backgroundColor: '#f8fafc', borderRadius: 8, padding: '5px 6px', textAlign: 'center', border: '1px solid #f1f5f9' }}>
              <p style={{ fontSize: 11, fontWeight: 800, color: '#0f172a' }}>{s.v}</p>
              <p style={{ fontSize: 7, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 1 }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Module grid */}
      <div style={{ padding: '10px 10px 0', flexShrink: 0 }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: '#0f172a', marginBottom: 7, letterSpacing: '-0.1px' }}>Your features</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {MODULES.map(m => (
            <div key={m.label} style={{ backgroundColor: m.color, borderRadius: 12, padding: '10px 6px', textAlign: 'center', border: `1px solid ${m.border}` }}>
              <div style={{ fontSize: 18, marginBottom: 4 }}>{m.icon}</div>
              <p style={{ fontSize: 8, fontWeight: 700, color: '#0f172a' }}>{m.label}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Upcoming banner */}
      <div style={{ margin: '10px 10px 0', padding: '8px 10px', backgroundColor: '#fff', borderRadius: 10, border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ef4444', animation: 'pulse 1.5s ease infinite' }} />
          <p style={{ fontSize: 10, fontWeight: 700, color: '#0f172a' }}>3 upcoming games this week</p>
          <span style={{ marginLeft: 'auto', fontSize: 9, color: '#F5E642', fontWeight: 600 }}>View →</span>
        </div>
      </div>
      <div style={{ flex: 1 }} />
      <BottomNav active="Home" />
    </div>
  )
}

// ── SCREEN 2: Schedule + RSVP ────────────────────────────────────────────────
function Screen2Schedule({ active }: { active: boolean }) {
  const [cardVisible, setCardVisible] = useState(false)

  useEffect(() => {
    if (!active) { setCardVisible(false); return }
    const t = setTimeout(() => setCardVisible(true), 900)
    return () => clearTimeout(t)
  }, [active])

  const today = 12
  const events: Record<number, 'game' | 'practice'> = { 5: 'practice', 9: 'game', 12: 'practice', 16: 'game', 19: 'practice', 23: 'game', 26: 'practice' }
  const weeks = [
    [null, null, 1, 2, 3, 4, 5],
    [6, 7, 8, 9, 10, 11, 12],
    [13, 14, 15, 16, 17, 18, 19],
    [20, 21, 22, 23, 24, 25, 26],
    [27, 28, 29, 30, null, null, null],
  ]

  return (
    <div style={{ background: '#f7f8fa', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' }}>
      <AppHeader title="Schedule" subtitle="Aurora Titans" />
      <div style={{ padding: '10px 10px 0', flex: 1 }}>
        {/* Month header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <p style={{ fontWeight: 800, fontSize: 13, color: '#1e293b' }}>April 2026</p>
          <div style={{ display: 'flex', gap: 4 }}>
            {['‹', '›'].map(ch => (
              <div key={ch} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#64748b' }}>{ch}</div>
            ))}
          </div>
        </div>
        {/* Day labels */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: 8, fontWeight: 700, color: '#94a3b8' }}>{d}</div>
          ))}
        </div>
        {/* Calendar grid */}
        {weeks.map((week, wi) => (
          <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
            {week.map((day, di) => {
              const ev = day ? events[day] : null
              const isToday = day === today
              return (
                <div key={di} style={{
                  aspectRatio: '1', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', borderRadius: 6,
                  background: isToday ? '#13201B' : day ? '#fff' : 'transparent',
                  border: day && !isToday ? '1px solid #f1f5f9' : 'none',
                }}>
                  {day && <span style={{ fontSize: 8, fontWeight: isToday ? 800 : 400, color: isToday ? '#F5E642' : '#1e293b' }}>{day}</span>}
                  {ev && <div style={{ width: 4, height: 4, borderRadius: 99, marginTop: 1, background: ev === 'game' ? '#ef4444' : '#22c55e' }} />}
                </div>
              )
            })}
          </div>
        ))}
        {/* Legend */}
        <div style={{ display: 'flex', gap: 10, margin: '6px 0 8px' }}>
          {[['#ef4444', 'Game'], ['#22c55e', 'Practice']].map(([c, l]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <div style={{ width: 5, height: 5, borderRadius: 99, background: c }} />
              <span style={{ fontSize: 8, color: '#94a3b8' }}>{l}</span>
            </div>
          ))}
        </div>
        {/* RSVP card — slides up */}
        <div style={{
          background: '#13201B', borderRadius: 12, padding: '10px 12px',
          transform: cardVisible ? 'translateY(0)' : 'translateY(24px)',
          opacity: cardVisible ? 1 : 0,
          transition: 'transform 380ms cubic-bezier(0.34,1.2,0.64,1), opacity 280ms ease',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
            <div>
              <p style={{ color: '#F5E642', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 3 }}>Tomorrow · 10:00 AM</p>
              <p style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>⚾ vs Eagles</p>
              <p style={{ color: '#475569', fontSize: 9, marginTop: 2 }}>Aurora Sports Complex</p>
            </div>
            <span style={{ fontSize: 18 }}>🏟️</span>
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
            <div style={{ flex: 1, backgroundColor: '#22c55e', borderRadius: 8, padding: '6px 0', textAlign: 'center' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>✓ Going</span>
            </div>
            <div style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '6px 0', textAlign: 'center', border: '1px solid rgba(255,255,255,0.08)' }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Can't make it</span>
            </div>
          </div>
        </div>
      </div>
      <BottomNav active="Schedule" />
    </div>
  )
}

// ── SCREEN 3: Payments ───────────────────────────────────────────────────────
function Screen3Payments({ active }: { active: boolean }) {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!active) { setProgress(0); return }
    const start = Date.now()
    const duration = 1100
    const timer = setInterval(() => {
      const p = Math.min((Date.now() - start) / duration, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      setProgress(Math.round(67 * eased))
      if (p >= 1) clearInterval(timer)
    }, 16)
    return () => clearInterval(timer)
  }, [active])

  return (
    <div style={{ background: '#f7f8fa', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' }}>
      <AppHeader title="Payments" subtitle="Aurora Titans" />
      <div style={{ padding: '10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Main payment card */}
        <div style={{ backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9' }}>
          <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>Spring Season Dues</p>
                <p style={{ fontSize: 9, color: '#94a3b8' }}>Due April 15, 2026</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 24, fontWeight: 900, color: '#0f172a', letterSpacing: '-1px', lineHeight: 1 }}>$150</p>
                <p style={{ fontSize: 8, color: '#94a3b8', marginTop: 2 }}>per family</p>
              </div>
            </div>
            {/* Progress */}
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: '#64748b' }}>8 of 12 families paid</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: '#22c55e' }}>{progress}%</span>
              </div>
              <div style={{ height: 6, backgroundColor: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: '100%', height: '100%', backgroundColor: '#22c55e', borderRadius: 99, transition: 'transform 16ms linear', transform: `scaleX(${progress / 100})`, transformOrigin: 'left' }} />
              </div>
            </div>
          </div>
          {/* Pay buttons */}
          <div style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ backgroundColor: '#000', borderRadius: 10, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', letterSpacing: '-0.2px' }}>  Pay</span>
            </div>
            <div style={{ backgroundColor: '#F5E642', borderRadius: 10, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#13201B' }}>Pay with card →</span>
            </div>
          </div>
        </div>

        {/* Paid item */}
        <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9' }}>
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textDecoration: 'line-through', marginBottom: 2 }}>Fall Registration</p>
            <p style={{ fontSize: 9, color: '#94a3b8' }}>Paid Mar 8 via Apple Pay</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>$125</span>
            <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: 'rgba(34,197,94,0.12)', border: '1.5px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </div>
        </div>
      </div>
      <BottomNav active="Pay" />
    </div>
  )
}

// ── SCREEN 4: Chat ────────────────────────────────────────────────────────────
type Msg = { type: 'coach' | 'parent' | 'system'; text: string; name?: string; time?: string }

function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 3, padding: '8px 10px', backgroundColor: '#f1f5f9', borderRadius: '14px 14px 14px 4px', width: 'fit-content' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: 5, height: 5, borderRadius: '50%', backgroundColor: '#94a3b8',
          animation: `bounce 1.2s ease infinite ${i * 0.15}s`,
        }} />
      ))}
    </div>
  )
}

const MESSAGES: (Msg & { delay: number })[] = [
  { delay: 150,  type: 'coach',  name: 'Coach R.', text: 'Great practice today! Game Saturday 10am 🔥', time: '3:14 PM' },
  { delay: 900,  type: 'system', text: '💰 Spring Dues ($150) due Apr 15 · Pay Now →' },
  { delay: 1700, type: 'parent', name: 'Sarah M.', text: "We'll be there! Go Titans! ⚾", time: '3:16 PM' },
  { delay: 2500, type: 'coach',  name: 'Coach R.', text: 'See everyone Saturday! 🏆', time: '3:17 PM' },
]

function Screen4Chat({ active }: { active: boolean }) {
  const [visible, setVisible] = useState<number[]>([])
  const [typing, setTyping] = useState(false)

  useEffect(() => {
    if (!active) { setVisible([]); setTyping(false); return }
    const timers: ReturnType<typeof setTimeout>[] = []

    MESSAGES.forEach((msg, i) => {
      // Show typing 300ms before each message
      timers.push(setTimeout(() => setTyping(true), msg.delay - 300 < 0 ? 0 : msg.delay - 300))
      timers.push(setTimeout(() => { setTyping(false); setVisible(v => [...v, i]) }, msg.delay))
    })

    return () => timers.forEach(t => clearTimeout(t))
  }, [active])

  return (
    <div style={{ background: '#f7f8fa', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' }}>
      <AppHeader title="Aurora Titans" subtitle="Team Chat · 14 members" />
      <div style={{ flex: 1, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, overflow: 'hidden', justifyContent: 'flex-end' }}>
        {MESSAGES.map((msg, i) => {
          if (!visible.includes(i)) return null
          if (msg.type === 'system') {
            return (
              <div key={i} style={{ textAlign: 'center', margin: '2px 0' }}>
                <span style={{ fontSize: 9, color: '#0f172a', backgroundColor: 'rgba(250,204,21,0.15)', border: '1px solid rgba(250,204,21,0.3)', borderRadius: 999, padding: '3px 10px', fontWeight: 600 }}>
                  {msg.text}
                </span>
              </div>
            )
          }
          const isCoach = msg.type === 'coach'
          return (
            <div key={i} style={{ display: 'flex', flexDirection: isCoach ? 'row' : 'row-reverse', gap: 6, alignItems: 'flex-end' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: isCoach ? 'linear-gradient(135deg,#13201B,#1B2A23)' : 'linear-gradient(135deg,#F5E642,#f59e0b)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 7, fontWeight: 800, color: isCoach ? '#F5E642' : '#13201B' }}>{isCoach ? 'CR' : 'SM'}</span>
              </div>
              <div style={{ maxWidth: '72%' }}>
                {msg.name && <p style={{ fontSize: 8, color: '#94a3b8', marginBottom: 2, paddingLeft: isCoach ? 2 : 0, paddingRight: isCoach ? 0 : 2, textAlign: isCoach ? 'left' : 'right' }}>{msg.name}</p>}
                <div style={{ backgroundColor: isCoach ? '#fff' : '#F5E642', borderRadius: isCoach ? '14px 14px 14px 4px' : '14px 14px 4px 14px', padding: '8px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: isCoach ? '1px solid #f1f5f9' : 'none' }}>
                  <p style={{ fontSize: 11, color: isCoach ? '#0f172a' : '#13201B', lineHeight: 1.4 }}>{msg.text}</p>
                </div>
                {msg.time && <p style={{ fontSize: 7.5, color: '#cbd5e1', marginTop: 2, paddingLeft: isCoach ? 2 : 0, paddingRight: isCoach ? 0 : 2, textAlign: isCoach ? 'left' : 'right' }}>{msg.time}</p>}
              </div>
            </div>
          )
        })}
        {typing && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#13201B,#1B2A23)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 7, fontWeight: 800, color: '#F5E642' }}>CR</span>
            </div>
            <TypingDots />
          </div>
        )}
      </div>
      {/* Input bar */}
      <div style={{ backgroundColor: '#fff', borderTop: '1px solid #f1f5f9', padding: '8px 10px', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        <div style={{ flex: 1, backgroundColor: '#f8fafc', borderRadius: 20, padding: '7px 12px', border: '1px solid #f1f5f9' }}>
          <span style={{ fontSize: 10, color: '#cbd5e1' }}>Message the team...</span>
        </div>
        <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: '#F5E642', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#13201B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </div>
      </div>
    </div>
  )
}

// ── SCREEN 5: Game Day — simple +/- team scoreboard ───────────────────────────
function Screen5GameDay({ active }: { active: boolean }) {
  const [score, setScore] = useState(3)
  const [pulse, setPulse] = useState(true)

  useEffect(() => {
    if (!active) { setScore(3); return }
    const t1 = setTimeout(() => setScore(4), 1600)
    return () => clearTimeout(t1)
  }, [active])

  useEffect(() => {
    const t = setInterval(() => setPulse(p => !p), 800)
    return () => clearInterval(t)
  }, [])

  return (
    <div style={{ background: '#07090F', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif', color: '#fff' }}>
      {/* Header */}
      <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#ef4444', opacity: pulse ? 1 : 0.3, transition: 'opacity 0.3s' }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.1em' }}>LIVE</span>
          </div>
          <span style={{ fontSize: 10, fontWeight: 600, color: '#475569' }}>vs Eagles · Top 4th</span>
        </div>
        {/* Score */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', marginTop: 8 }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 8, color: '#334155', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Eagles</p>
            <span style={{ fontSize: 40, fontWeight: 900, color: '#475569', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>2</span>
          </div>
          <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 900, padding: '0 10px' }}>–</span>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 8, color: '#F5E642', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Titans</p>
            <span style={{ fontSize: 40, fontWeight: 900, color: '#F5E642', fontVariantNumeric: 'tabular-nums', lineHeight: 1, transition: 'all 200ms ease' }}>{score}</span>
          </div>
        </div>
      </div>

      {/* BSO live count — read-only display (kept for baseball, owner #294).
          No play-by-play feed, batting averages, or pitch-by-pitch input —
          Game Day is a simple +/- team scoreboard. */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '10px 14px' }}>
        {[
          { label: 'B', count: 2, max: 3, color: '#22c55e' },
          { label: 'S', count: 1, max: 2, color: '#F5E642' },
          { label: 'O', count: 1, max: 2, color: '#ef4444' },
        ].map(({ label, count, max, color }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b' }}>{label}</span>
            <div style={{ display: 'flex', gap: 3 }}>
              {Array.from({ length: max }).map((_, i) => (
                <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: i < count ? color : 'rgba(255,255,255,0.1)' }} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Simple +/- score controls — one tap to bump the score */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 8, flexShrink: 0 }}>
        <button style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '11px 0', color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 700 }}>+ Eagles run</button>
        <button onClick={() => setScore(s => s + 1)} style={{ flex: 1, backgroundColor: '#F5E642', border: 'none', borderRadius: 10, padding: '11px 0', color: '#13201B', fontSize: 11, fontWeight: 800 }}>+ Titans run</button>
      </div>
    </div>
  )
}

// ── SCREEN 6: Treasury ────────────────────────────────────────────────────────
function useCountUp(target: number, active: boolean, duration = 1300) {
  const [v, setV] = useState(0)
  useEffect(() => {
    if (!active) { setV(0); return }
    const start = Date.now()
    const t = setInterval(() => {
      const p = Math.min((Date.now() - start) / duration, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      setV(Math.round(target * eased))
      if (p >= 1) clearInterval(t)
    }, 16)
    return () => clearInterval(t)
  }, [active, target, duration])
  return v
}

function Screen6Treasury({ active }: { active: boolean }) {
  const collected   = useCountUp(2400, active)
  const outstanding = useCountUp(600,  active)
  const net         = useCountUp(2388, active)

  const stats = [
    { label: 'Collected',   value: collected,   color: '#22c55e', prefix: '$' },
    { label: 'Outstanding', value: outstanding, color: '#f97316', prefix: '$' },
    { label: 'Expenses',    value: 0,           color: '#94a3b8', prefix: '$' },
    { label: 'Net Balance', value: net,         color: '#F5E642', prefix: '$' },
  ]

  return (
    <div style={{ background: '#f7f8fa', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' }}>
      <AppHeader title="Treasury" subtitle="Aurora Titans" />
      <div style={{ padding: '10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Stat grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
          {stats.map(s => (
            <div key={s.label} style={{ backgroundColor: '#fff', borderRadius: 14, padding: '12px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <p style={{ fontSize: 8, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>{s.label}</p>
              <p style={{ fontSize: 20, fontWeight: 900, color: s.color, letterSpacing: '-1px', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                {s.prefix}{s.value.toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        {/* Payment rows */}
        <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9' }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Collection Status</p>
          {[
            { name: 'Spring Dues', paid: 8, total: 12, amount: '$150' },
            { name: 'Tournament Fee', paid: 12, total: 12, amount: '$75' },
          ].map(row => (
            <div key={row.name} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: '#0f172a' }}>{row.name}</span>
                <span style={{ fontSize: 9, color: '#94a3b8' }}>{row.paid}/{row.total} · {row.amount}</span>
              </div>
              <div style={{ height: 5, backgroundColor: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${(row.paid / row.total) * 100}%`, height: '100%', backgroundColor: row.paid === row.total ? '#22c55e' : '#F5E642', borderRadius: 99 }} />
              </div>
            </div>
          ))}
        </div>

        {/* Export button */}
        <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9' }}>
          <div>
            <p style={{ fontSize: 10, fontWeight: 600, color: '#0f172a', marginBottom: 1 }}>Export Report</p>
            <p style={{ fontSize: 8, color: '#94a3b8' }}>Download full season summary</p>
          </div>
          <div style={{ backgroundColor: '#13201B', borderRadius: 8, padding: '6px 10px' }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: '#F5E642' }}>CSV ↓</span>
          </div>
        </div>
      </div>
      <BottomNav active="More" />
    </div>
  )
}

// ── Phone frame ───────────────────────────────────────────────────────────────
const SCREENS = [
  { Screen: Screen1Dashboard, name: 'Dashboard' },
  { Screen: Screen2Schedule,  name: 'Schedule'  },
  { Screen: Screen3Payments,  name: 'Payments'  },
  { Screen: Screen4Chat,      name: 'Chat'      },
  { Screen: Screen5GameDay,   name: 'Game Day'  },
  { Screen: Screen6Treasury,  name: 'Treasury'  },
]

export interface DemoPhoneProps {
  /** Show the feature label list on the left (desktop) */
  showFeatureList?: boolean
  /** Show the "Try it free" CTA below the phone */
  showCTA?: boolean
  /** Additional CSS class for the wrapper */
  className?: string
}

export function DemoPhone({ showFeatureList = true, showCTA = true, className = '' }: DemoPhoneProps) {
  const [activeIdx, setActiveIdx] = useState(0)
  const [visible, setVisible]     = useState(true)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pausedRef = useRef(false)

  const goTo = useCallback((idx: number) => {
    if (idx === activeIdx) return
    setVisible(false)
    setTimeout(() => {
      setActiveIdx(idx)
      setVisible(true)
    }, FADE_MS)
  }, [activeIdx])

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      if (pausedRef.current) return
      setVisible(false)
      setTimeout(() => {
        setActiveIdx(prev => (prev + 1) % SCREENS.length)
        setVisible(true)
      }, FADE_MS)
    }, SCREEN_MS)
  }, [])

  useEffect(() => {
    startTimer()
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [startTimer])

  function handleDotClick(idx: number) {
    pausedRef.current = true
    goTo(idx)
    if (timerRef.current) clearInterval(timerRef.current)
    setTimeout(() => {
      pausedRef.current = false
      startTimer()
    }, 7000)
  }

  const { Screen } = SCREENS[activeIdx]

  return (
    <>
      {/* Keyframes */}
      <style>{`
        @keyframes demo-float {
          0%, 100% { transform: rotate(-2deg) translateY(0px); }
          50%       { transform: rotate(-2deg) translateY(-6px); }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40%            { transform: translateY(-4px); }
        }
      `}</style>

      <div className={className} style={{ display: 'flex', alignItems: 'center', gap: 48 }}>

        {/* Feature list — desktop only */}
        {showFeatureList && (
          <div className="hidden lg:flex" style={{ flexDirection: 'column', gap: 12 }}>
            {FEATURES.map((f, i) => {
              const isActive = i === activeIdx
              return (
                <button
                  key={f.label}
                  onClick={() => handleDotClick(i)}
                  style={{
                    all: 'unset', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderRadius: 10,
                    backgroundColor: isActive ? 'rgba(250,204,21,0.08)' : 'transparent',
                    border: `1px solid ${isActive ? 'rgba(250,204,21,0.2)' : 'transparent'}`,
                    transition: 'all 200ms ease',
                  }}
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>{f.icon}</span>
                  <span style={{ fontSize: 14, fontWeight: isActive ? 700 : 500, color: isActive ? '#ffffff' : '#64748b', transition: 'color 200ms ease' }}>
                    {isActive ? '→ ' : '✓ '}{f.label}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {/* Phone + dots */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>

          {/* Label above */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 12px', borderRadius: 999,
            backgroundColor: 'rgba(250,204,21,0.1)',
            border: '1px solid rgba(250,204,21,0.2)',
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ef4444', animation: 'bounce 1.4s ease infinite' }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#F5E642', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Live Demo
            </span>
          </div>

          {/* Phone frame */}
          <div
            style={{ animation: 'demo-float 3.5s ease-in-out infinite' }}
            onMouseEnter={() => { pausedRef.current = true }}
            onMouseLeave={() => { pausedRef.current = false }}
          >
            {/* Titanium body */}
            <div style={{
              width: 300, height: 650,
              position: 'relative',
              borderRadius: 44,
              background: 'linear-gradient(160deg, #2e2e44 0%, #1a1a2e 100%)',
              boxShadow: [
                '0 0 0 1px #3d3d5c',
                'inset 0 0 0 1px rgba(255,255,255,0.06)',
                '0 40px 80px rgba(0,0,0,0.6)',
                '0 20px 40px rgba(0,0,0,0.4)',
              ].join(', '),
            }}>
              {/* Buttons */}
              <div style={{ position: 'absolute', left: -3, top: 100, width: 3, height: 26, backgroundColor: '#252538', borderRadius: '2px 0 0 2px' }} />
              <div style={{ position: 'absolute', left: -3, top: 138, width: 3, height: 48, backgroundColor: '#252538', borderRadius: '2px 0 0 2px' }} />
              <div style={{ position: 'absolute', left: -3, top: 196, width: 3, height: 48, backgroundColor: '#252538', borderRadius: '2px 0 0 2px' }} />
              <div style={{ position: 'absolute', right: -3, top: 150, width: 3, height: 62, backgroundColor: '#252538', borderRadius: '0 2px 2px 0' }} />

              {/* Screen glass */}
              <div style={{
                position: 'absolute',
                top: 14, left: 14, right: 14, bottom: 14,
                borderRadius: 36,
                overflow: 'hidden',
                backgroundColor: '#f7f8fa',
              }}>
                {/* Status bar */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 50, zIndex: 10, pointerEvents: 'none', backgroundColor: activeIdx === 4 ? '#060c16' : '#fff', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 10 }}>
                  <div style={{ width: 90, height: 27, backgroundColor: '#000', borderRadius: 18 }} />
                  <span style={{ position: 'absolute', left: 18, top: 15, fontSize: 11, fontWeight: 600, color: activeIdx === 4 ? 'rgba(255,255,255,0.8)' : '#0f172a', letterSpacing: '-0.2px', fontFamily: '-apple-system, sans-serif' }}>9:41</span>
                  <div style={{ position: 'absolute', right: 16, top: 17, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
                      {[4, 6, 8, 10].map((h, i) => (
                        <div key={i} style={{ width: 2, height: h, backgroundColor: activeIdx === 4 ? 'rgba(255,255,255,0.7)' : '#0f172a', borderRadius: 1, opacity: i < 3 ? 1 : 0.3 }} />
                      ))}
                    </div>
                    <div style={{ width: 17, height: 8, border: `1.2px solid ${activeIdx === 4 ? 'rgba(255,255,255,0.5)' : '#0f172a55'}`, borderRadius: 2, padding: '1px', display: 'flex', alignItems: 'center' }}>
                      <div style={{ width: '75%', height: '100%', backgroundColor: activeIdx === 4 ? 'rgba(255,255,255,0.7)' : '#0f172a', borderRadius: 1 }} />
                    </div>
                  </div>
                </div>

                {/* App content */}
                <div style={{ position: 'absolute', top: 50, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(10px)',
                    transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
                  }}>
                    <Screen active={visible && activeIdx === SCREENS.findIndex(s => s.Screen === Screen)} />
                  </div>
                </div>

                {/* Home indicator */}
                <div style={{ position: 'absolute', bottom: 7, left: '50%', transform: 'translateX(-50%)', width: 100, height: 4, backgroundColor: activeIdx === 4 ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)', borderRadius: 2, zIndex: 10 }} />
              </div>
            </div>
          </div>

          {/* Progress dots */}
          <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
            {SCREENS.map((_, i) => (
              <button
                key={i}
                onClick={() => handleDotClick(i)}
                style={{
                  all: 'unset', cursor: 'pointer',
                  width: i === activeIdx ? 20 : 7,
                  height: 7,
                  borderRadius: 99,
                  backgroundColor: i === activeIdx ? '#F5E642' : 'rgba(255,255,255,0.15)',
                  transition: 'all 300ms cubic-bezier(0.34,1.2,0.64,1)',
                }}
              />
            ))}
          </div>

          {/* CTA */}
          {showCTA && (
            <a
              href="/signup"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                height: 46, padding: '0 28px', borderRadius: 12,
                backgroundColor: '#F5E642', color: '#13201B',
                fontWeight: 800, fontSize: 15, textDecoration: 'none',
                boxShadow: '0 0 0 0 rgba(250,204,21,0)',
                transition: 'box-shadow 200ms ease, opacity 150ms ease',
              }}
            >
              Try it free →
            </a>
          )}
        </div>
      </div>
    </>
  )
}
